import moment from 'moment-timezone';
import type { Culture, DateLocalizer, DateRange } from 'react-big-calendar';

import { TIME_FORMATS } from './calendar-events.const';

/**
 * CalendarFormatUtil - I format dates and time ranges for calendar display
 * ONLY concerned with date/time formatting - no event state, no API calls, no rendering
 */
export const CalendarFormatUtil = {
  /**
   * Formats the date range header for calendar views
   * Shows "Month Day - Day" if same month, or "Month Day - Month Day" if different months
   *
   * @example
   * Same month: "January 1-15"
   * Different months: "January 31 - February 5"
   */
  formatDayRangeHeader(
    { start, end }: DateRange,
    culture?: Culture,
    localizer?: DateLocalizer
  ): string {
    const sameMonth = moment(start).month() === moment(end).month();
    const startFormat = TIME_FORMATS.MONTH_DAY;
    const endFormat = sameMonth ? 'D' : startFormat;

    return `${localizer?.format(start, TIME_FORMATS.MONTH_DAY, culture)}${
      sameMonth ? '-' : ' - '
    }${localizer?.format(end, endFormat, culture)}`;
  },

  /**
   * Formats individual day headers in calendar views
   * Shows abbreviated day name and date (e.g., "Mon 1/15")
   */
  formatDay(date: Date, culture?: Culture, localizer?: DateLocalizer): string {
    return localizer?.format(date, TIME_FORMATS.MONTH_ABBR_DAY, culture) || '';
  },

  /**
   * Formats event time ranges shown inside event blocks and the
   * drag-to-select overlay. AM/PM is intentionally dropped — the time
   * gutter on the left of the calendar provides am/pm context for the
   * row, so labels read e.g. "9:00 - 9:30".
   */
  formatEventTimeRange(
    { start, end }: { start: Date; end: Date },
    culture?: Culture,
    localizer?: DateLocalizer
  ): string {
    return `${localizer?.format(
      start,
      TIME_FORMATS.HOUR_MINS,
      culture
    )} - ${localizer?.format(end, TIME_FORMATS.HOUR_MINS, culture)}`;
  },
};
